import { useMemo, useRef } from 'react';
import { TableCellComponentProps, type ImagePreviewCellValue } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import {
  getTableCellClasses,
  getTableCellProps,
  handleCellClick,
  getContentAlignClass,
  isFlexColumn,
} from '../../utils/cellStyles';
import { Popup } from '@/modules/Popup';

function parseImagePreviewValue(value: unknown): ImagePreviewCellValue | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string') {
    const s = value.trim();
    return s ? { src: s } : null;
  }
  if (
    typeof value === 'object' &&
    'src' in value &&
    typeof (value as { src: unknown }).src === 'string'
  ) {
    const o = value as ImagePreviewCellValue;
    const s = o.src.trim();
    if (!s) return null;
    let previewAspectInPopup: '1:1' | '16:9' | '4:3' | undefined;
    if (o.previewAspectInPopup === '1:1' || o.previewAspectInPopup === '16:9' || o.previewAspectInPopup === '4:3') {
      previewAspectInPopup = o.previewAspectInPopup;
    }
    return { src: s, title: o.title, alt: o.alt, previewAspectInPopup };
  }
  return null;
}

/**
 * Celda `imagePreview`: miniatura **16:9** (`aspect-video`) con altura máx. **fila − 16 px**;
 * el **contenedor** de la grilla usa `rounded-control-xs`, `overflow-hidden`, borde (brand al hover) y
 * la **`<img>`** en absoluto llenando la caja. En el **Popup** (hover) la imagen a **ancho 300** a ras,
 * **sin** padding alrededor ni borde extra en el recuadro de imagen, `showPanelBorder` desactivado en
 * el `Popup`. `self-stretch` para el `%` no colapse. Contrasta con `IconCell` + lightbox al clic.
 *
 * El `accessor` puede ser una **URL** (`string`) o {@link ImagePreviewCellValue}. En el **Popup**, la
 * proporción del recuadro de imagen admite `previewAspectInPopup` (`'1:1' | '16:9' | '4:3'`; la
 * miniatura 16:9 de la grilla no cambia).
 */
export function ImagePreviewCell({
  value,
  row: _row,
  column,
  state = 'default',
  className,
  onCellFocus,
  stickyLeft = 0,
  isLastLocked = false,
  isRowEditing,
  hasColumnMaxWidthAuto = false,
}: TableCellComponentProps) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const parsed = useMemo(() => parseImagePreviewValue(value), [value]);

  const baseClasses = getTableCellClasses(
    state,
    className,
    column.locked,
    isLastLocked,
    undefined,
    isRowEditing,
    false,
    column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto),
  );
  const cellProps = getTableCellProps(column, stickyLeft, undefined, false, hasColumnMaxWidthAuto);
  const alignClass = getContentAlignClass(column.contentAlign);
  const hasFixedWidth = column.width !== undefined;

  const imgAlt = parsed?.alt ?? parsed?.title ?? '';
  const popupAspect: '1:1' | '16:9' | '4:3' = parsed?.previewAspectInPopup ?? '1:1';
  const popupImageBoxClass = cn(
    'ui-relative ui-w-full ui-min-w-0 ui-shrink-0 ui-overflow-hidden',
    popupAspect === '1:1' && 'ui-aspect-square',
    popupAspect === '16:9' && 'ui-aspect-video',
    popupAspect === '4:3' && 'ui-aspect-[4/3]',
  );
  const popupImgDimensions =
    popupAspect === '16:9' ? { width: 300, height: 169 } : popupAspect === '4:3' ? { width: 300, height: 225 } : { width: 300, height: 300 };

  return (
    <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
      <div
        className={cn(
          'ui-flex ui-h-full ui-min-h-0 ui-w-full ui-items-center',
          alignClass,
          hasFixedWidth && 'ui-min-w-0 ui-overflow-visible',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {!parsed ? (
          <span className="ui-text-body-xs-regular ui-text-secondary">—</span>
        ) : (
          <div className="ui-relative ui-flex ui-shrink-0 ui-self-stretch ui-items-center">
            <div
              ref={triggerRef}
              className={cn(
                'ui-relative ui-box-border ui-aspect-video ui-h-[calc(100%-16px)] ui-w-auto ui-max-w-full',
                'ui-overflow-hidden ui-rounded-control-xs',
                'ui-cursor-pointer ui-border ui-border-default ui-bg-fill-secondary-default',
                'ui-transition-colors ui-duration-200 ui-outline-none',
                'hover:ui-border-brand',
              )}
            >
              <img
                src={parsed.src}
                alt={imgAlt}
                draggable={false}
                className="ui-absolute ui-inset-0 ui-block ui-h-full ui-w-full ui-object-cover"
              />
            </div>
            <Popup
              triggerRef={triggerRef}
              position="bottom-left"
              openOnHover
              hoverAnchorFollowPointer
              openOnHoverAllowPanelPointer={false}
              popoverArrow={false}
              closeOnClick={false}
              showPanelBorder={false}
            >
              <div
                className={cn(
                  'ui-flex ui-w-[300px] ui-max-w-[300px] ui-flex-col ui-overflow-hidden',
                  'ui-rounded-control-xs',
                )}
              >
                <div
                  className={popupImageBoxClass}
                  data-image-preview-popup-aspect={popupAspect}
                >
                  <img
                    src={parsed.src}
                    alt={imgAlt}
                    width={popupImgDimensions.width}
                    height={popupImgDimensions.height}
                    draggable={false}
                    className="ui-absolute ui-inset-0 ui-block ui-h-full ui-w-full ui-object-cover"
                  />
                </div>
                {parsed.title ? (
                  <p
                    className="ui-line-clamp-3 ui-px-3 ui-pb-2 ui-pt-2 ui-text-body-xs-regular ui-text-secondary"
                  >
                    {parsed.title}
                  </p>
                ) : null}
              </div>
            </Popup>
          </div>
        )}
      </div>
    </td>
  );
}

ImagePreviewCell.displayName = 'ImagePreviewCell';
